"use client";

import React from "react";
import { ResponsiveModal, MODAL_IDS } from "@wtc/ui";
import { Message } from "@/lib/core/crm/types";
import { Client } from "@/lib/types/database";
import { ComposeForm } from "./ComposeForm";
import { ComposeFormValues } from "@/lib/types/compose-modal-types";
import { EmailRecipient } from "../ClientEmailSelector";
import { TicketSummary } from "@/lib/types/database";

export type ComposeMessageFormValues = ComposeFormValues;

interface ComposeModalWrapperProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  originalMessage?: Message | null;
  onSubmit: (values: ComposeMessageFormValues) => void;
  isSending: boolean;
  initialRecipient?: string;
  initialClient?: Client;
  initialRecipients?: EmailRecipient[];
  /** Addresses to pre-seed as BCC (e.g. a support inbox's group address). */
  initialBccRecipients?: EmailRecipient[];
  initialSubject?: string;
  initialBody?: string;
  selectedTicket?: TicketSummary | null;
  onMessageSent?: () => void;
  onFollowUpTaskModalClose?: (open: boolean) => void;
  globalModalId?: string;
  isForwardMode?: boolean;
}

/**
 * Modal surface for the compose form. This is now a thin wrapper: it supplies
 * the `ResponsiveModal` chrome and delegates ALL form logic to the shared,
 * surface-agnostic {@link ComposeForm}. Inline surfaces (sequences/today
 * composer pane) render `ComposeForm` directly with a different shell — one
 * code path, two chromes. `deferBody` preserves the modal's open-animation
 * defer; `onMessageSent`/`onFollowUpTaskModalClose` are handled by the
 * container's follow-up flow and are intentionally not consumed here.
 */
export const ComposeModalWrapper = React.memo(function ComposeModalWrapper(
  props: ComposeModalWrapperProps
) {
  const {
    isOpen,
    onOpenChange,
    originalMessage,
    onSubmit,
    isSending,
    initialRecipient,
    initialClient,
    initialRecipients,
    initialBccRecipients,
    initialSubject,
    initialBody,
    selectedTicket,
    globalModalId = MODAL_IDS.messages.compose,
    isForwardMode = false,
  } = props;

  // Stable renderShell reference so ComposeForm's React.memo can short-circuit
  // (MEMO_STABLE_PROPS). Closes over globalModalId / isForwardMode / originalMessage.
  const renderShell = React.useCallback(
    ({
      children,
      hasUnfilledVariables,
    }: {
      children: React.ReactNode;
      hasUnfilledVariables: boolean;
    }) => (
      <ResponsiveModal
        globalModalId={globalModalId}
        title={
          isForwardMode
            ? "Forward Message"
            : originalMessage
              ? "Reply to Message"
              : "Compose Message"
        }
        description={
          hasUnfilledVariables
            ? "Some template variables need your attention before sending."
            : undefined
        }
        maxWidth="90vw"
        className="flex flex-col"
      >
        {children}
      </ResponsiveModal>
    ),
    [globalModalId, isForwardMode, originalMessage]
  );

  return (
    <ComposeForm
      isOpen={isOpen}
      originalMessage={originalMessage}
      onSubmit={onSubmit}
      isSending={isSending}
      initialRecipient={initialRecipient}
      initialClient={initialClient}
      initialRecipients={initialRecipients}
      initialBccRecipients={initialBccRecipients}
      initialSubject={initialSubject}
      initialBody={initialBody}
      selectedTicket={selectedTicket}
      onOpenChange={onOpenChange}
      globalModalId={globalModalId}
      isForwardMode={isForwardMode}
      deferBody
      showFullScreenButton
      renderShell={renderShell}
    />
  );
});
